var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState } from 'react';
import { Table, TableContainer, TableHead, TableBody, TableRow, TableCell, TableFooter, TableFooterPagination, TableFooterShowMore, TableSortLabel, } from '@one-thd/sui-atomic-components';
import { useProAllowance } from '../../../hooks/use-proallowance';
import { AllowanceFrequency, ACTIONS, } from '../../../models/proallowance';
import { PRO_ALLOWANCE_DISABLED_SUCCESS_MSG, PRO_ALLOWANCE_DELETED_SUCCESS_MSG, } from '../../../constants/response-success-messages';
import { ERR_SAVE_CHANGES } from '../../../constants/response-error-messages';
import { TAlertStatus, TAlertType, } from '../../../models/common/alert-messages';
import { ProAllowanceTableRow } from './pro-allowance-table-row';
import { getAllowanceStatus } from '../../../utils/helpers';
import { PRO_GET_PAGINATION_CLICK, PRO_TABLE_VIEW_ROWS, } from '../../../constants/analytics';
import { track, getTableViewAnalyticsOption } from '../../../utils/analytics';
var sortTableColumns = function (field, newSortDirection, allowances) {
    var _a;
    switch (field) {
        case 'allowanceUsed':
        case 'allowanceAmount':
            allowances.sort(function (aVal, bVal) {
                var aValue = field === 'allowanceUsed'
                    ? parseFloat(aVal.allowanceUsed)
                    : parseFloat(aVal.allowanceAmount);
                var bValue = field === 'allowanceUsed'
                    ? parseFloat(bVal.allowanceUsed)
                    : parseFloat(bVal.allowanceAmount);
                return newSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            });
            break;
        case 'frequencyExpirationTs': {
            allowances.sort(function (aVal, bVal) {
                var aValue = !aVal.frequencyExpirationTs
                    ? Infinity
                    : new Date(aVal.frequencyExpirationTs).getTime();
                var bValue = !bVal.frequencyExpirationTs
                    ? Infinity
                    : new Date(bVal.frequencyExpirationTs).getTime();
                return newSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            });
            break;
        }
        case 'cardNumberLast4': {
            allowances.sort(function (aVal, bVal) {
                var aValue = !aVal.cardNumberLast4
                    ? Infinity
                    : parseInt(aVal.cardNumberLast4, 10);
                var bValue = !bVal.cardNumberLast4
                    ? Infinity
                    : parseInt(bVal.cardNumberLast4, 10);
                return newSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            });
            break;
        }
        case 'status': {
            var displayStatusValueMap_1 = {
                Active: 0,
                Disabled: 1,
                Expired: 2,
            };
            allowances.sort(function (aVal, bVal) {
                var aValue = displayStatusValueMap_1[getAllowanceStatus(aVal.status, Boolean(aVal.assignedFirstName && aVal.assignedLastName), aVal.cardNumberLast4 || '', aVal.cardStatus || '')];
                var bValue = displayStatusValueMap_1[getAllowanceStatus(bVal.status, Boolean(bVal.assignedFirstName && bVal.assignedLastName), bVal.cardNumberLast4 || '', bVal.cardStatus || '')];
                return newSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            });
            break;
        }
        case 'frequency': {
            var displayStatusValueMap_2 = (_a = {},
                _a[AllowanceFrequency.NO_RECURRENCE] = 0,
                _a[AllowanceFrequency.DAILY] = 1,
                _a[AllowanceFrequency.WEEKLY] = 2,
                _a[AllowanceFrequency.MONTHLY] = 3,
                _a);
            allowances.sort(function (aVal, bVal) {
                var aValue = displayStatusValueMap_2[aVal.frequency];
                var bValue = displayStatusValueMap_2[bVal.frequency];
                return newSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            });
            break;
        }
        case 'assignedFirstName':
        case 'assignedLastName':
        case 'createdByFirstName':
        case 'createdByLastName':
            allowances.sort(function (aVal, bVal) {
                var aName = aVal[field];
                var bName = bVal[field] || '';
                // anchor "User removed" to end of list regardless of sort direction
                if (!aName)
                    return 0;
                return newSortDirection === 'asc'
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName);
            });
            break;
        default:
            allowances.sort(function (aVal, bVal) {
                var aValue = (aVal[field] || '').toString();
                var bValue = (bVal[field] || '').toString();
                return newSortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            });
            break;
    }
    return allowances;
};
var CELL_WIDTH = {
    NAME: 160,
    SPENT_AMOUNT: 112,
    PRO_ALLOWANCE_AMOUNT: 112,
    RESET_FREQUENCY: 132,
    RESETS_ON: 107,
    CARD: 179,
    CREATED_BY: 147,
    EXPIRES: 107,
    STATUS: 109,
    ACTIONS: 90,
};
var DEFAULT_ROWS_PER_PAGE = 10;
export var ProAllowanceTable = function (_a) {
    var allowances = _a.allowances;
    var _b = __read(useState(0), 2), page = _b[0], setPage = _b[1];
    var _c = __read(useState(DEFAULT_ROWS_PER_PAGE), 2), rowsPerPage = _c[0], setRowsPerPage = _c[1];
    var _d = __read(React.useState('status'), 2), sortedBy = _d[0], setSortedBy = _d[1];
    var _e = __read(React.useState('asc'), 2), sortDirection = _e[0], setSortDirection = _e[1];
    var _f = __read(useState(function () {
        return sortTableColumns(sortedBy, sortDirection, allowances).slice(0, DEFAULT_ROWS_PER_PAGE);
    }), 2), visibleRows = _f[0], setVisibleRows = _f[1];
    var _g = useProAllowance(), triggerGetAllowances = _g.triggerGetAllowances, setAlertMessage = _g.setAlertMessage;
    var onRowsPerPageChangeHandler = function (event, rows) {
        var _a;
        setPage(0);
        setRowsPerPage(rows);
        setVisibleRows(allowances.slice(0, rows));
        var textContent = ((_a = event.target.textContent) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || '';
        track(PRO_TABLE_VIEW_ROWS, {
            target: getTableViewAnalyticsOption(textContent, rowsPerPage, rows),
        });
    };
    var onPageChangeHandler = function (_event, newPage) {
        setPage(newPage);
        setVisibleRows(allowances.slice(newPage * rowsPerPage, newPage * rowsPerPage + rowsPerPage));
        track(PRO_GET_PAGINATION_CLICK, {
            rows: rowsPerPage,
            pageNumber: newPage + 1,
        });
    };
    var sortRows = function (field, newSortDirection) {
        var sortedRows = sortTableColumns(field, newSortDirection, allowances);
        setVisibleRows(sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
    };
    var sortByField = function (field) {
        var newSortDirection = sortedBy === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortDirection(newSortDirection);
        setSortedBy(field);
        sortRows(field, newSortDirection);
    };
    var nameSortDirection = sortedBy === 'assignedFirstName' ? sortDirection : null;
    var spentAmtSortDirection = sortedBy === 'allowanceUsed' ? sortDirection : null;
    var proAllowSortDirection = sortedBy === 'allowanceAmount' ? sortDirection : null;
    var resetFreqSortDirection = sortedBy === 'frequency' ? sortDirection : null;
    var resetOnSortDirection = sortedBy === 'frequencyExpirationTs' ? sortDirection : null;
    var cardSortDirection = sortedBy === 'cardNumberLast4' ? sortDirection : null;
    var createdBySortDirection = sortedBy === 'createdByFirstName' ? sortDirection : null;
    var expiresSortDirection = sortedBy === 'expirationTs' ? sortDirection : null;
    var statusSortDirection = sortedBy === 'status' ? sortDirection : null;
    var onSuccess = function (action) {
        var message = '';
        if (action === ACTIONS.DISABLE) {
            message = PRO_ALLOWANCE_DISABLED_SUCCESS_MSG.message;
        }
        else if (action === ACTIONS.DELETE) {
            message = PRO_ALLOWANCE_DELETED_SUCCESS_MSG.message;
        }
        setAlertMessage({
            alertStatus: TAlertStatus.SUCCESS,
            type: TAlertType.MIDDLE,
            text: message,
        });
        triggerGetAllowances();
    };
    var onError = function () {
        setAlertMessage({
            alertStatus: TAlertStatus.ERROR,
            type: TAlertType.MIDDLE,
            text: ERR_SAVE_CHANGES.message,
        });
    };
    return (React.createElement("div", { className: "sui-w-full sui-overflow-y-auto sui-no-scrollbar" },
        React.createElement(TableContainer, null,
            React.createElement(Table, { layout: "fixed", density: "normal", "data-testid": "pro-allowance-table" },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.NAME, sticky: "horizontal", sortDirection: nameSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by name", active: sortedBy === 'assignedFirstName', direction: nameSortDirection, onClick: function () { return sortByField('assignedFirstName'); } }, "Name")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.SPENT_AMOUNT, sortDirection: spentAmtSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by spent amount", active: sortedBy === 'allowanceUsed', direction: spentAmtSortDirection, onClick: function () { return sortByField('allowanceUsed'); } }, "Spent Amount")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.PRO_ALLOWANCE_AMOUNT, sortDirection: proAllowSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by pro allowance amount", active: sortedBy === 'allowanceAmount', direction: proAllowSortDirection, onClick: function () { return sortByField('allowanceAmount'); } }, "Pro Allowance Amount")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.RESET_FREQUENCY, sortDirection: resetFreqSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by reset frequency", active: sortedBy === 'frequency', direction: resetFreqSortDirection, onClick: function () { return sortByField('frequency'); } }, "Reset Frequency")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.RESETS_ON, sortDirection: resetOnSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by reset on", active: sortedBy === 'frequencyExpirationTs', direction: resetOnSortDirection, onClick: function () { return sortByField('frequencyExpirationTs'); } }, "Resets On")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.CARD, sortDirection: cardSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card", active: sortedBy === 'cardNumberLast4', direction: cardSortDirection, onClick: function () { return sortByField('cardNumberLast4'); } }, "Card")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.CREATED_BY, sortDirection: createdBySortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by created by", active: sortedBy === 'createdByFirstName', direction: createdBySortDirection, onClick: function () { return sortByField('createdByFirstName'); } }, "Created By")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.EXPIRES, sortDirection: expiresSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by expires", active: sortedBy === 'expirationTs', direction: expiresSortDirection, onClick: function () { return sortByField('expirationTs'); } }, "Expires")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.STATUS, sortDirection: statusSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by status", active: sortedBy === 'status', direction: statusSortDirection, onClick: function () { return sortByField('status'); } }, "Status")),
                        React.createElement(TableCell, { width: CELL_WIDTH.ACTIONS }, "Actions"))),
                React.createElement(TableBody, null, visibleRows.map(function (allowance) { return (React.createElement(ProAllowanceTableRow, { key: allowance.allowanceId, allowance: allowance, onMeatballActionSuccess: onSuccess, onMeatballActionError: onError })); })))),
        React.createElement(React.Fragment, null,
            allowances.length > 100 && (React.createElement(TableFooter, { component: "div" },
                React.createElement("div", { className: "sui-flex sui-justify-center" },
                    React.createElement(TableFooterPagination, { "data-testid": "ProAllowanceTableFooterPagination100", page: page, count: allowances.length, rowsPerPage: DEFAULT_ROWS_PER_PAGE, onPageChange: onPageChangeHandler, onRowsPerPageChange: onRowsPerPageChangeHandler, disableViewAll: true })))),
            allowances.length > 25 && allowances.length <= 100 && (React.createElement(TableFooter, { component: "div" },
                React.createElement("div", { className: "sui-flex sui-justify-center" },
                    React.createElement(TableFooterPagination, { "data-testid": "ProAllowanceTableFooterPagination25", page: page, count: allowances.length, rowsPerPage: DEFAULT_ROWS_PER_PAGE, onPageChange: onPageChangeHandler, onRowsPerPageChange: onRowsPerPageChangeHandler })))),
            allowances.length <= 25 && (React.createElement(TableFooter, { component: "div" },
                React.createElement("div", { className: "sui-flex md:sui-justify-center" },
                    React.createElement(TableFooterShowMore, { "data-testid": "TableFooterShowMore", rowsPerPage: DEFAULT_ROWS_PER_PAGE, onRowsPerPageChange: onRowsPerPageChangeHandler, count: allowances.length })))))));
};
